import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdDefinitionKey } from '../basic-properties/hd-chart-properties.types';
import { HD_DEFINITION_INFO_FILES, HD_DEFINITION_INFO_ORDER } from './hd-chart-basics-info-metadata';
import { HdChartBasicsInfoPanelMode, hdOpenAllInfoPanel } from './hd-chart-basics-info-panels.util';
import { HdChartBasicsInfoService } from './hd-chart-basics-info.service';
import { HdInfoSectionsComponent } from './hd-info-sections.component';
import { HdInfoSectionVm, HdInfoSectionsVm } from './hd-info-sections.models';

/** Paneel-data: de definitie van de actieve chart voedt de "jouw definitie"-markering. */
export interface HdDefinitionInfoPanelData {
  readonly definitionKey: HdDefinitionKey | null;
  readonly mode?: HdChartBasicsInfoPanelMode;
}

/**
 * Info-paneel "Definitie": algemeen document (wat definitie is, brugpoorten)
 * en de eigen definitie; "Toon alle definities" opent het overzichtspaneel
 * (modus `all`): single t/m viervoudig plus "geen" als inklapbare lijst.
 */
@Component({
  selector: 'hd-definition-info-panel',
  standalone: true,
  imports: [HdInfoSectionsComponent],
  template: '<hd-info-sections [vm]="vm()" (showAllRequested)="openAll()" />',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdDefinitionInfoPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdDefinitionInfoPanelData | null> | null;
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly info = inject(HdChartBasicsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly ownKey = computed<HdDefinitionKey | null>(() => this.panelData?.()?.definitionKey ?? null);
  private readonly mode = computed<HdChartBasicsInfoPanelMode>(() => this.panelData?.()?.mode ?? 'own');

  protected readonly vm = computed<HdInfoSectionsVm>(() => {
    const lang = this.lang();
    const t = (key: string) => this.info.text(`definition.${key}`, lang);
    const ownKey = this.ownKey();
    const sections: HdInfoSectionVm[] = HD_DEFINITION_INFO_ORDER.map(key => ({
      key,
      label: this.info.propertyText(`definitionValues.${key}`, lang),
      url: this.info.markdownUrl('definitions', HD_DEFINITION_INFO_FILES[key], lang),
      isOwn: key === ownKey,
      badges: key === ownKey ? [t('ownBadgeLabel')] : [],
      facts: [
        { label: t('facts.structure'), value: t(`structure.${key}`) },
        { label: t('facts.share'), value: t(`share.${key}`) },
      ],
    }));
    if (this.mode() === 'all') {
      return { title: t('allTitle'), sections, collapsible: true };
    }
    const own = sections.find(section => section.isOwn);
    return {
      title: t('title'),
      jumpToStripLabel: own ? t('jumpToOwnLabel') : undefined,
      generalUrl: this.info.markdownUrl('definitions', 'general', lang),
      strip: own
        ? { label: t('stripLabel'), showAllLabel: t('showAllLabel'), chips: [{ sectionKey: own.key, label: own.label }] }
        : undefined,
      sections,
    };
  });

  protected openAll(): void {
    hdOpenAllInfoPanel<HdDefinitionInfoPanelComponent, HdDefinitionInfoPanelData>(this.floatingPanels, this.panelRef, {
      key: 'definition-info-all',
      component: HdDefinitionInfoPanelComponent,
      data: { definitionKey: this.ownKey(), mode: 'all' },
      tabLabel: this.info.text('definition.allTabLabel', this.lang()),
    });
  }
}
